import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { type FieldError, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { Dropdown } from '../../../shared/components/dropdown/dropdown';
import { Field } from '../../../shared/components/field/field';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Input } from '../../../shared/components/input/input';
import { Radio } from '../../../shared/components/radio/radio';
import { useCurrentCurrency } from '../../../shared/hooks/use-current-currency';
import { useToast } from '../../../shared/hooks/use-toast';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import { slugify } from '../../../shared/utils/strings';
import type { IUserLimit } from '../../limits.interface';
import { UserLimitPeriod } from '../../limits.interface';
import {
    IBudgetLimitsTransactionType,
    ICreatorName,
    useAddUserBudgetLimitMutation,
} from '../../queries/budget-limit.graphql';
import { FormSection } from '../form-section/form-section';

import styles from './deposit-limit-form.module.scss';

const style = createStyleHelper(styles, 'deposit-limit-form');

interface IBudgetLimitFormProps {
    availableCurrencies: string[];
}

function errorMessage(
    errors: Partial<Record<keyof IUserLimit, FieldError | undefined>>,
    errorType: keyof IUserLimit,
): string {
    const message = errors?.[errorType]?.message;
    const type = errors?.[errorType]?.type;
    if (message) {
        return message;
    } else if (type) {
        return `account.limits.error.${slugify(type)}`;
    }
    return '';
}

export const DepositLimitForm: FunctionComponent<IBudgetLimitFormProps> = ({
    availableCurrencies,
}) => {
    const intl = useIntl();
    const auth = useAuth();
    const toast = useToast();
    const profileId = auth.user?.profile?.user_profile_id as string;
    const currency = useCurrentCurrency();

    const {
        register,
        handleSubmit,
        formState: { isDirty, isValid, errors },
        reset,
    } = useForm<IUserLimit>({
        mode: 'onChange',
        defaultValues: {
            currencyCode: currency,
        },
    });

    const [{ fetching }, execute] = useAddUserBudgetLimitMutation();

    const onSubmit = useCallback(
        async (limitData: IUserLimit) => {
            const { error } = await execute({
                userProfileId: profileId,
                xTenantID: config.TENANT_ID,
                creatorName: ICreatorName.User,
                limitInfo: {
                    amount: limitData.amount,
                    currencyCode: limitData.currencyCode || availableCurrencies[0] || currency,
                    createdBy: profileId,
                    period: limitData.period,
                    type: limitData.type as unknown as IBudgetLimitsTransactionType,
                    mediatorType: '',
                },
            });

            if (error) {
                toast.error(getErrorMessage(intl, 'user.limits.create', error));
                return;
            }

            toast.success(
                intl.formatMessage({
                    id: 'user.limit.created.success',
                    defaultMessage: 'User Limit Created',
                }),
            );
            reset();
        },
        [execute, profileId, availableCurrencies, currency, toast, intl, reset],
    );

    const amountError = errorMessage(errors, 'amount');

    return (
        <form className={style()} onSubmit={handleSubmit(onSubmit)}>
            <FormSection title="deposit-limit-form.type.title">
                <div className={style('limit')}>
                    <Radio
                        {...register('type', {
                            required: true,
                        })}
                        value={IBudgetLimitsTransactionType.Deposit}
                        defaultValue={true}
                        name="type"
                        bold
                        label={
                            <FormattedMessage
                                id="deposit-limit-form.deposit-limit.name"
                                defaultMessage="Deposit Limit"
                            />
                        }
                    />
                    <p className={style('limit-description')}>
                        <FormattedMessage
                            id="deposit-limit-form.deposit-limit.description"
                            defaultMessage="The deposit limit you set refers to the amount of money you will allow yourself to deposit during a defined period."
                        />
                    </p>
                </div>
            </FormSection>

            <FormSection title="deposit-limit-form.duration.title">
                <Radio
                    {...register('period', {
                        required: true,
                    })}
                    value={UserLimitPeriod.P1D}
                    className={style('duration')}
                    name="period"
                    label={
                        <FormattedMessage
                            id="deposit-limit-form.duration.one-day"
                            defaultMessage="24 hours"
                        />
                    }
                />
                <Radio
                    {...register('period', {
                        required: true,
                    })}
                    value={UserLimitPeriod.P7D}
                    className={style('duration')}
                    name="period"
                    label={
                        <FormattedMessage
                            id="deposit-limit-form.duration.one-week"
                            defaultMessage="7 days"
                        />
                    }
                />
                <Radio
                    {...register('period', {
                        required: true,
                    })}
                    value={UserLimitPeriod.P30D}
                    className={style('duration')}
                    name="period"
                    label={
                        <FormattedMessage
                            id="deposit-limit-form.duration.thirty-days"
                            defaultMessage="30 days"
                        />
                    }
                />
                <Field
                    className={style('amount')}
                    label={
                        <FormattedMessage
                            id="deposit-limit-form.amount.label"
                            defaultMessage="Amount"
                        />
                    }
                    message={amountError ? intl.formatMessage({ id: amountError }) : undefined}
                >
                    <Input
                        type="number"
                        {...register('amount', {
                            required: true,
                            valueAsNumber: true,
                        })}
                    />
                </Field>
                {availableCurrencies.length > 1 ? (
                    <Field
                        className={style('currency')}
                        label={
                            <FormattedMessage
                                id="deposit-limit-form.currency.label"
                                defaultMessage="Currency"
                            />
                        }
                    >
                        <Dropdown
                            items={availableCurrencies.map((currency) => ({
                                value: currency,
                                name: intl.formatMessage({
                                    id: `currency.${currency}.label`,
                                    defaultMessage: currency,
                                }),
                            }))}
                            {...register('currencyCode', { required: true })}
                        />
                    </Field>
                ) : null}
            </FormSection>

            <Button
                size={ButtonSize.LARGE}
                type="submit"
                loading={fetching}
                disabled={!isDirty || !isValid}
            >
                <FormattedMessage id="deposit-limit-form.submit" defaultMessage="Save Changes" />
            </Button>
        </form>
    );
};
